<template>
  <div class="rx-table-wrap">
    <table class="rx-table">
      <thead>
        <tr>
          <th class="rx-col-check">
            <input
              ref="selectAll"
              type="checkbox"
              class="rx-checkbox"
              aria-label="Select all"
              :checked="allSelected"
              :disabled="selectableItems.length === 0 || pending"
              @change="$emit('select-all', $event.target.checked)" />
          </th>
          <th>Name</th>
          <th class="rx-col-contents">Contents</th>
          <th class="rx-col-size">Size</th>
          <th class="rx-col-modified">Modified</th>
          <th class="rx-col-by">By</th>
          <th class="rx-col-actions"><span class="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="creating" class="rx-table__row rx-table__row--edit">
          <td class="rx-col-check"></td>
          <td colspan="6" class="rx-col-edit">
            <div class="rx-edit">
              <span class="rx-icon rx-icon--folder"><i class="fas fa-folder"></i></span>
              <input
                ref="createInput"
                v-model="createName"
                type="text"
                class="rx-edit__input"
                placeholder="Folder name — use / to nest"
                aria-label="New folder name. Use / to nest folders"
                maxlength="400"
                :disabled="pending"
                @keydown.enter.prevent="saveCreate"
                @keydown.esc.prevent="$emit('create-cancel')" />
              <button type="button" class="rx-edit__btn rx-edit__btn--ok" :disabled="pending" aria-label="Create folder" title="Create" @click="saveCreate"><i :class="pending ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i></button>
              <button type="button" class="rx-edit__btn" :disabled="pending" aria-label="Cancel" title="Cancel" @click="$emit('create-cancel')"><i class="fas fa-times"></i></button>
            </div>
            <div v-if="error" class="rx-edit__error" role="alert">{{ error }}</div>
          </td>
        </tr>
        <tr v-if="loading && items.length === 0">
          <td colspan="7" class="rx-table__empty">Loading…</td>
        </tr>
        <tr v-else-if="items.length === 0 && !creating">
          <td colspan="7" class="rx-table__empty">{{ emptyText }}</td>
        </tr>
        <tr v-for="item in items" :key="item.id" :class="['rx-table__row', item.isArchived ? 'is-archived' : '', selected[item.id] ? 'is-selected' : '']">
          <td class="rx-col-check">
            <input
              v-if="selectable(item)"
              type="checkbox"
              class="rx-checkbox"
              :checked="!!selected[item.id]"
              :disabled="pending"
              :aria-label="'Select ' + item.name"
              @change="$emit('toggle-select', item)" />
            <input v-else type="checkbox" class="rx-checkbox" disabled title="System folders can't be selected" :aria-label="'Select ' + item.name" />
          </td>
          <td v-if="item.id === renamingId" colspan="5" class="rx-col-edit">
            <div class="rx-edit">
              <span :class="['rx-icon', item.kind === 'folder' ? 'rx-icon--folder' : 'rx-icon--file']"><i :class="item.kind === 'folder' ? 'fas fa-folder' : fileIcon(item)"></i></span>
              <input
                ref="renameInput"
                v-model="renameName"
                type="text"
                class="rx-edit__input"
                :aria-label="'New name for ' + item.name"
                maxlength="200"
                :disabled="pending"
                @keydown.enter.prevent="saveRename(item)"
                @keydown.esc.prevent="$emit('rename-cancel')" />
              <button type="button" class="rx-edit__btn rx-edit__btn--ok" :disabled="pending" aria-label="Save name" title="Save" @click="saveRename(item)"><i :class="pending ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i></button>
              <button type="button" class="rx-edit__btn" :disabled="pending" aria-label="Cancel" title="Cancel" @click="$emit('rename-cancel')"><i class="fas fa-times"></i></button>
            </div>
            <div v-if="error" class="rx-edit__error" role="alert">{{ error }}</div>
          </td>
          <td v-if="item.id !== renamingId" class="rx-col-name">
            <a href="#" class="rx-name" :title="item.name" @click.prevent="$emit('open', item)">
              <span :class="['rx-icon', item.kind === 'folder' ? 'rx-icon--folder' : 'rx-icon--file']">
                <i :class="item.kind === 'folder' ? 'fas fa-folder' : fileIcon(item)"></i>
              </span>
              <span class="rx-name__text">{{ item.name }}</span>
              <span v-if="item.isArchived" class="rx-pill">Archived</span>
            </a>
          </td>
          <td v-if="item.id !== renamingId" class="rx-col-contents">{{ contents(item) }}</td>
          <td v-if="item.id !== renamingId" class="rx-col-size">{{ size(item) }}</td>
          <td v-if="item.id !== renamingId" class="rx-col-modified">{{ modified(item) }}</td>
          <td v-if="item.id !== renamingId" class="rx-col-by">{{ by(item) }}</td>
          <td class="rx-col-actions">
            <b-dropdown
              v-if="actionable(item)"
              right
              no-caret
              lazy
              variant="link"
              class="rx-menu"
              toggle-class="rx-kebab rx-kebab--active"
              menu-class="rx-menu__list"
              :disabled="pending">
              <template #button-content>
                <i class="fas fa-ellipsis-v"></i><span class="sr-only">Actions for {{ item.name }}</span>
              </template>
              <template v-for="a in actionsFor(item)">
                <b-dropdown-divider v-if="a.divider" :key="'d-' + a.key"></b-dropdown-divider>
                <b-dropdown-item-button
                  :key="a.key"
                  :disabled="a.disabled"
                  :title="a.disabled ? a.sub : null"
                  @click="$emit('action', a.key, item)">
                  <span :class="['rx-menu__title', a.danger ? 'rx-menu__title--danger' : '']">{{ a.title }}</span>
                  <span v-if="a.sub" class="rx-menu__sub">{{ a.sub }}</span>
                </b-dropdown-item-button>
              </template>
            </b-dropdown>
            <button v-else type="button" class="rx-kebab" disabled title="No actions available" :aria-label="'Actions for ' + item.name">
              <i class="fas fa-ellipsis-v"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { nodeMeta, formatDate, shortPersonName, itemActions, hasItemActions, canSelect } from './resourcesData'
import { nodeSize } from './fileVersions'

export default {
  name: 'ContentsTable',
  props: {
    items: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'This folder is empty' },
    // Inline "new folder" row at the top of the table
    creating: { type: Boolean, default: false },
    // Folder id whose name cell is being edited
    renamingId: { type: String, default: null },
    pending: { type: Boolean, default: false },
    error: { type: String, default: '' },
    // Multi-select: { [id]: true }
    selected: { type: Object, default: function () { return {} } }
  },
  data () {
    return {
      createName: '',
      renameName: ''
    }
  },
  computed: {
    selectableItems () {
      return this.items.filter(function (i) { return canSelect(i) })
    },
    selectedCount () {
      var sel = this.selected
      return this.selectableItems.filter(function (i) { return !!sel[i.id] }).length
    },
    allSelected () {
      return this.selectableItems.length > 0 && this.selectedCount === this.selectableItems.length
    }
  },
  watch: {
    selectedCount: function () {
      this.syncIndeterminate()
    },
    allSelected: function () {
      this.syncIndeterminate()
    },
    creating: function (on) {
      var self = this
      if (!on) return
      this.createName = ''
      this.$nextTick(function () {
        if (self.$refs.createInput) self.$refs.createInput.focus()
      })
    },
    renamingId: function (id) {
      var self = this
      if (!id) return
      var item = this.items.filter(function (i) { return i.id === id })[0]
      this.renameName = item ? item.name : ''
      this.$nextTick(function () {
        var el = self.$refs.renameInput
        if (Array.isArray(el)) el = el[0]
        if (el) {
          el.focus()
          el.select()
        }
      })
    }
  },
  methods: {
    syncIndeterminate () {
      var el = this.$refs.selectAll
      if (el) el.indeterminate = this.selectedCount > 0 && !this.allSelected
    },
    actionable (item) {
      return hasItemActions(item)
    },
    actionsFor (item) {
      return itemActions(item)
    },
    selectable (item) {
      return canSelect(item)
    },
    saveCreate () {
      if (this.pending) return
      this.$emit('create-save', this.createName)
    },
    saveRename (item) {
      if (this.pending) return
      this.$emit('rename-save', item, this.renameName)
    },
    contents (item) {
      return nodeMeta(item, null)
    },
    size (item) {
      return nodeSize(item)
    },
    modified (item) {
      return formatDate(item.modified, 'long')
    },
    by (item) {
      return shortPersonName(item.modifiedBy)
    },
    fileIcon (item) {
      if (item.mediaType === 'Image') return 'far fa-file-image'
      if (item.mediaType === 'Video') return 'far fa-file-video'
      if (item.fileType === 'PDF') return 'far fa-file-pdf'
      return 'far fa-file'
    }
  }
}
</script>

<style scoped>
.rx-table-wrap {
  width: 100%;
  overflow-x: auto;
}
.rx-table {
  width: 100%;
  border-collapse: collapse;
  color: #1f2937;
}
.rx-table th {
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #6b7280;
  font-weight: 600;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e5ea;
  text-align: left;
  white-space: nowrap;
}
.rx-table td {
  height: 56px;
  padding: 0 12px;
  border-bottom: 1px solid #eef0f2;
  vertical-align: middle;
  font-size: 14px;
}
.rx-table__row:hover td {
  background: #f7f8fa;
}
.rx-table__empty {
  text-align: center;
  color: #6b7280;
}
.rx-col-check {
  width: 40px;
}
.rx-col-contents,
.rx-col-size,
.rx-col-modified,
.rx-col-by {
  color: #6b7280;
  white-space: nowrap;
}
.rx-col-actions {
  width: 48px;
  text-align: right;
}
.rx-col-name {
  max-width: 0;
  width: 50%;
}
.rx-name {
  display: flex;
  align-items: center;
  color: #1f2937;
  font-weight: 500;
  text-decoration: none;
  min-width: 0;
}
.rx-name:hover .rx-name__text {
  text-decoration: underline;
}
.rx-name__text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-icon {
  flex: 0 0 auto;
  margin-right: 10px;
  font-size: 16px;
}
.rx-icon--folder {
  color: #2f6fa3;
}
.rx-icon--file {
  color: #6b7280;
}
.rx-kebab {
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #6b7280;
  border-radius: 6px;
}
.rx-col-edit {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.rx-edit {
  display: flex;
  align-items: center;
}
.rx-edit__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #1a7f86;
  border-radius: 6px;
  font-size: 16px;
  color: #1f2937;
  outline: none;
}
.rx-edit__btn {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  margin-left: 6px;
  border: 1px solid #e2e5ea;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
}
.rx-edit__btn--ok {
  background: #1e7e34;
  border-color: #1e7e34;
  color: #fff;
}
.rx-edit__btn:disabled {
  opacity: 0.6;
}
.rx-edit__error {
  margin: 4px 0 0 26px;
  font-size: 13px;
  color: #b91c1c;
}
.rx-menu >>> .rx-kebab {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rx-kebab--active,
.rx-menu >>> .rx-kebab--active {
  color: #4b5563;
}
.rx-menu >>> .rx-menu__list {
  min-width: 280px;
}
.rx-menu >>> .dropdown-item {
  min-height: 44px;
  white-space: normal;
}
.rx-menu__title {
  display: block;
  font-size: 14px;
}
.rx-menu__sub {
  display: block;
  font-size: 12px;
  color: #6b7280;
}
.rx-kebab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.rx-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}
.rx-checkbox:disabled {
  cursor: not-allowed;
}
.rx-table__row.is-selected td {
  background: #e3f1ef;
}
.rx-table__row.is-archived .rx-col-name,
.rx-table__row.is-archived .rx-col-contents,
.rx-table__row.is-archived .rx-col-size,
.rx-table__row.is-archived .rx-col-modified,
.rx-table__row.is-archived .rx-col-by {
  opacity: 0.55;
}
.rx-pill {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
.rx-menu__title--danger {
  color: #b91c1c;
}
</style>
